import { useEffect, useRef, useCallback, useState } from 'react';
import { useEditor, ROOT_NODE } from '@craftjs/core';
import { DRAGGED_ELEMENT_CHANGE } from '@/pages/editor/CustomEventHandler';
import {
  CONTENT_BLOCK_NAME,
  POPUP_NODE_NAME,
  NEW_ELEMENT_DEFAULT_HEIGHTS,
} from '@/utils/constants';

/**
 * DropTargetIndicator - Visual feedback for drag & drop operations.
 * Shows a styled placeholder at the drop position:
 * - Page level: full-width placeholder matching sibling container height
 * - Empty container: highlights the container itself (no size change)
 * - Container with children: placeholder matches sibling flex properties
 */

const POSITION_CHANGE_DEBOUNCE_MS = 50;
const PARENT_CHANGE_DEBOUNCE_MS = 60;
const INITIAL_SHOW_DELAY_MS = 50; // Delay before showing placeholder for the first time
const DEFAULT_CONTAINER_MIN_HEIGHT = 40;
const DEFAULT_NEW_ELEMENT_HEIGHT = 60;
const PAGE_LEVEL_MIN_HEIGHT = 200;

export const DropTargetIndicator: React.FC = () => {
  const rafRef = useRef<number | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastStateRef = useRef<string>('');
  const pendingStateRef = useRef<string>('');
  const lastParentIdRef = useRef<string | null>(null);
  const [draggedElementDisplayName, setDraggedElementDisplayName] = useState<string | null>(null);
  const [draggedElementDimensions, setDraggedElementDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Listen for dragged element changes
  useEffect(() => {
    const handleDraggedElementChange = (
      event: CustomEvent<{
        displayName: string | null;
        dimensions: { width: number; height: number } | null;
      }>,
    ) => {
      setDraggedElementDisplayName(event.detail.displayName);
      setDraggedElementDimensions(event.detail.dimensions ?? null);
    };

    window.addEventListener(DRAGGED_ELEMENT_CHANGE, handleDraggedElementChange as EventListener);
    return () => {
      window.removeEventListener(
        DRAGGED_ELEMENT_CHANGE,
        handleDraggedElementChange as EventListener,
      );
    };
  }, []);

  const {
    indicator,
    enabled,
    parentChildCount,
    parentFlexDirection,
    parentId,
    isPageParent,
    draggedNodeDoms,
  } = useEditor((state) => {
    let parentChildCount = 0;
    let parentFlexDirection = 'column';
    let parentId: string | null = null;
    let isPageParent = false;

    if (state.indicator?.placement?.parent?.id) {
      parentId = state.indicator.placement.parent.id;
      const parentNode = state.nodes[parentId];
      if (parentNode) {
        parentChildCount = parentNode.data.nodes?.length || 0;
        const props = parentNode.data.props || {};
        parentFlexDirection = props.flexDirection || 'column';
        isPageParent =
          parentId === ROOT_NODE ||
          parentNode.data.type === 'Page' ||
          parentNode.data.displayName === 'Page';
      }
    }

    // Get DOM elements of dragged nodes and check if they belong to the drop target parent
    const draggedNodeDoms: HTMLElement[] = [];
    if (state.events?.dragged && parentId) {
      const parentNode = state.nodes[parentId];
      const parentChildren = parentNode?.data?.nodes || [];
      for (const nodeId of Array.from(state.events.dragged)) {
        if (parentChildren.includes(nodeId)) {
          const dom = state.nodes[nodeId]?.dom;
          if (dom) {
            draggedNodeDoms.push(dom as HTMLElement);
          }
        }
      }
    }

    return {
      indicator: state.indicator,
      enabled: state.options.enabled,
      parentChildCount,
      parentFlexDirection,
      parentId,
      isPageParent,
      draggedNodeDoms,
    };
  });

  const cleanup = useCallback(() => {
    document.querySelectorAll('#drop-preview-placeholder').forEach((el) => el.remove());

    // Remove blank page drag class
    document.querySelectorAll('.blank-page-drag-active').forEach((el) => {
      el.classList.remove('blank-page-drag-active');
    });

    document.querySelectorAll('[data-drop-target]').forEach((el) => {
      const htmlEl = el as HTMLElement;
      const originalStyles = htmlEl.getAttribute('data-original-styles') || '';
      htmlEl.setAttribute('style', originalStyles);
      htmlEl.removeAttribute('data-drop-target');
      htmlEl.removeAttribute('data-original-styles');
    });

    document.querySelectorAll('[data-hidden-for-drag]').forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.display = htmlEl.getAttribute('data-original-display') ?? '';
      htmlEl.style.visibility = htmlEl.getAttribute('data-original-visibility') ?? '';
      htmlEl.style.border = htmlEl.getAttribute('data-original-border') ?? '';
      htmlEl.removeAttribute('data-hidden-for-drag');
      htmlEl.removeAttribute('data-original-display');
      htmlEl.removeAttribute('data-original-visibility');
      htmlEl.removeAttribute('data-original-border');
    });

    document.querySelectorAll('[data-overflow-visible-for-drag]').forEach((el) => {
      const htmlEl = el as HTMLElement;
      const originalOverflow = htmlEl.getAttribute('data-original-overflow') || '';
      htmlEl.style.overflow = originalOverflow;
      htmlEl.removeAttribute('data-overflow-visible-for-drag');
      htmlEl.removeAttribute('data-original-overflow');
    });
  }, []);

  const updatePlaceholder = useCallback(
    (stateKey: string) => {
      if (!indicator?.placement || !indicator.placement.parent?.dom) {
        cleanup();
        return;
      }

      const { placement } = indicator;
      const parentDom = placement.parent.dom as HTMLElement;
      const isHorizontal = parentFlexDirection === 'row';
      const isEmptyContainer = parentChildCount === 0;
      const isPopupDrag = draggedElementDisplayName === POPUP_NODE_NAME;

      // Check if this is an invalid drop (non-container on blank page)
      const isBlankPage = isPageParent && isEmptyContainer;
      const isInvalidBlankPageDrop =
        isBlankPage &&
        draggedElementDisplayName !== undefined &&
        draggedElementDisplayName !== null &&
        draggedElementDisplayName !== CONTENT_BLOCK_NAME &&
        draggedElementDisplayName !== POPUP_NODE_NAME;

      // BLANK PAGE or POPUP: highlight entire page area (full width/height)
      if (isBlankPage || isPopupDrag) {
        const pageDom = (parentDom.closest('.page-node') as HTMLElement | null) ?? parentDom;

        // Add class FIRST for instant content hiding via CSS
        pageDom.classList.add('blank-page-drag-active');

        cleanup();
        lastStateRef.current = stateKey;
        pageDom.setAttribute('data-drop-target', 'true');
        const originalStyles = pageDom.getAttribute('style') || '';
        pageDom.setAttribute('data-original-styles', originalStyles);

        // Re-add class after cleanup (cleanup removes it)
        pageDom.classList.add('blank-page-drag-active');

        if (isInvalidBlankPageDrop) {
          // Warning style for invalid drops - red with 10% opacity background
          pageDom.style.background = 'rgba(239, 68, 68, 0.1)'; // Red 10% opacity
          pageDom.style.border = '2px solid #EF4444'; // Red border
          pageDom.style.boxSizing = 'border-box'; // Prevent size change
        } else {
          // Valid drop style - purple with 10% opacity background
          pageDom.style.background = 'rgba(98, 69, 242, 0.1)'; // Purple 10% opacity
          pageDom.style.border = '2px solid #6245F2'; // Purple border
          pageDom.style.boxSizing = 'border-box'; // Prevent size change
        }

        // Hide page's dashed indicator to prevent double border effect
        pageDom.setAttribute('data-hidden-for-drag', 'true');

        // Set overflow visible to prevent border clipping
        const originalOverflow = window.getComputedStyle(pageDom).overflow;
        pageDom.setAttribute('data-overflow-visible-for-drag', 'true');
        pageDom.setAttribute('data-original-overflow', originalOverflow);
        pageDom.style.overflow = 'visible';
        return;
      }

      let flexContainer: HTMLElement;
      if (isPageParent) {
        // Page: find the page-content div (direct child with the flex container class)
        flexContainer = (parentDom.querySelector('.page-content') as HTMLElement) || parentDom;
      } else {
        // Use :scope to find only DIRECT child .container-content, not nested ones
        // Container structure: parentDom > container-content-outer > container-content
        flexContainer =
          (parentDom.querySelector(
            ':scope > .container-content-outer > .container-content',
          ) as HTMLElement) || parentDom;
      }

      cleanup();
      lastStateRef.current = stateKey;

      const placeholder = document.createElement('div');
      placeholder.id = 'drop-preview-placeholder';

      const baseStyles = `
        background: #ECEAFC;
        border: 2px solid #6245F2;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        pointer-events: none;
        box-sizing: border-box;
        overflow: hidden;
        z-index: 10;
      `;

      // PAGE LEVEL - only containers can be dropped directly on Page
      if (isPageParent) {
        // Block all non-container drops on Page
        const isNonContainerDrop =
          draggedElementDisplayName !== undefined &&
          draggedElementDisplayName !== null &&
          draggedElementDisplayName !== CONTENT_BLOCK_NAME;

        if (isNonContainerDrop) {
          // Don't show placeholder - only containers allowed on Page
          cleanup();
          return;
        }

        const craftChildren = Array.from(flexContainer.children).filter((child) => {
          const el = child as HTMLElement;
          return el.id !== 'drop-preview-placeholder' && el.nodeType === Node.ELEMENT_NODE;
        }) as HTMLElement[];

        let insertIndex = 0;
        if (placement.where === 'before') {
          insertIndex = placement.index ?? 0;
        } else if (placement.where === 'after') {
          insertIndex = (placement.index ?? 0) + 1;
        }

        // Page level: use dragged element height when available, otherwise sibling or default
        const pageReferenceChild =
          craftChildren.length > 0
            ? craftChildren[Math.min(insertIndex, craftChildren.length - 1)]
            : undefined;
        const pagePlaceholderHeight =
          draggedElementDimensions?.height ??
          pageReferenceChild?.getBoundingClientRect().height ??
          PAGE_LEVEL_MIN_HEIGHT;

        placeholder.style.cssText = `
          width: 100%;
          height: ${pagePlaceholderHeight}px;
          min-height: ${PAGE_LEVEL_MIN_HEIGHT}px;
          flex: 0 0 auto;
          ${baseStyles}
        `;

        if (craftChildren.length === 0 || insertIndex >= craftChildren.length) {
          flexContainer.appendChild(placeholder);
        } else {
          const targetChild = craftChildren[insertIndex];
          flexContainer.insertBefore(placeholder, targetChild);
        }
        return;
      }

      // EMPTY CONTAINER: highlight the container itself (no size change)
      if (isEmptyContainer) {
        // Use parentDom directly instead of flexContainer to highlight the actual hovered container
        const targetContainer = parentDom;

        targetContainer.setAttribute('data-drop-target', 'true');
        const originalStyles = targetContainer.getAttribute('style') || '';
        targetContainer.setAttribute('data-original-styles', originalStyles);
        targetContainer.style.background = 'rgba(98, 69, 242, 0.1)'; // Purple 10% opacity
        targetContainer.style.border = '2px solid #6245F2'; // Purple border
        targetContainer.style.borderRadius = '8px';
        targetContainer.style.boxSizing = 'border-box';

        // Set overflow visible to prevent border clipping
        const originalOverflow = window.getComputedStyle(targetContainer).overflow;
        targetContainer.setAttribute('data-overflow-visible-for-drag', 'true');
        targetContainer.setAttribute('data-original-overflow', originalOverflow);
        targetContainer.style.overflow = 'visible';

        // Hide container-content's dashed indicator to prevent double border effect
        // Container structure: parentDom > container-content-outer > container-content
        const containerContent = parentDom.querySelector(
          ':scope > .container-content-outer > .container-content',
        ) as HTMLElement;
        if (containerContent) {
          containerContent.setAttribute('data-hidden-for-drag', 'true');
        }

        parentDom
          .querySelectorAll(
            '[class*="BlankPlaceholder"], [class*="CanvasContentEmpty"], [class*="empty-state"]',
          )
          .forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.setAttribute('data-hidden-for-drag', 'true');
            htmlEl.style.visibility = 'hidden';
          });

        return;
      }
      // CONTAINER WITH CHILDREN: placeholder matches sibling flex properties
      // Get all direct children that are actual craft nodes (not utility elements)
      const craftChildren = Array.from(flexContainer.children).filter((child) => {
        const el = child as HTMLElement;
        // Exclude our placeholder and any non-element nodes
        if (el.id === 'drop-preview-placeholder' || el.nodeType !== Node.ELEMENT_NODE) {
          return false;
        }
        // Include any element that looks like a craft node
        // (has node-related classes or data attributes)
        return (
          el.classList.contains('container-node') ||
          el.classList.contains('image-node') ||
          el.classList.contains('text-node') ||
          el.classList.contains('button-node') ||
          el.classList.contains('video-node') ||
          el.classList.contains('form-node') ||
          el.classList.contains('countdown-node') ||
          el.classList.contains('icon-node') ||
          el.hasAttribute('data-cy') ||
          el.hasAttribute('data-node-id')
        );
      }) as HTMLElement[];

      if (craftChildren.length === 0) {
        const flexContainerRect = flexContainer.getBoundingClientRect();
        const emptyHeight = draggedElementDimensions?.height ?? DEFAULT_NEW_ELEMENT_HEIGHT;

        placeholder.style.cssText = `
          width: ${flexContainerRect.width}px;
          height: ${Math.max(emptyHeight, DEFAULT_CONTAINER_MIN_HEIGHT)}px;
          ${baseStyles}
        `;
        flexContainer.appendChild(placeholder);
        return;
      }

      let insertIndex = 0;
      if (placement.where === 'before') {
        insertIndex = placement.index ?? 0;
      } else if (placement.where === 'after') {
        insertIndex = (placement.index ?? 0) + 1;
      }

      // Pick the sibling closest to the insertion point for flex property reference
      const nearestIndex = Math.min(insertIndex, craftChildren.length - 1);
      const referenceSibling = craftChildren[nearestIndex];
      const siblingStyle = window.getComputedStyle(referenceSibling);
      const siblingRect = referenceSibling.getBoundingClientRect();

      const hasExactDimensions = draggedElementDimensions !== null;
      // For new elements from sidebar: use type-specific default height when available
      const typeDefaultHeight = draggedElementDisplayName
        ? NEW_ELEMENT_DEFAULT_HEIGHTS[draggedElementDisplayName]
        : undefined;
      const fallbackHeight = typeDefaultHeight ?? siblingRect.height;

      let flexStyles: string;

      if (isHorizontal) {
        const heightRule = hasExactDimensions
          ? `height: ${draggedElementDimensions.height}px;`
          : `align-self: stretch;`;

        // ROW: match sibling flex properties for width
        flexStyles = `
          flex-grow: ${siblingStyle.flexGrow || '1'};
          flex-shrink: ${siblingStyle.flexShrink || '1'};
          flex-basis: ${siblingStyle.flexBasis || '0%'};
          min-width: 0;
          ${heightRule}
          min-height: ${DEFAULT_CONTAINER_MIN_HEIGHT}px;
        `;
      } else {
        const targetHeight = hasExactDimensions ? draggedElementDimensions.height : fallbackHeight;

        // COLUMN: full width, use target height
        flexStyles = `
          flex: 0 0 auto;
          width: 100%;
          height: ${targetHeight}px;
          min-height: ${DEFAULT_CONTAINER_MIN_HEIGHT}px;
        `;
      }

      placeholder.style.cssText = `${flexStyles} ${baseStyles}`;

      // Hide parent container's border to prevent double border (1px parent + 2px placeholder = 3px)
      if (flexContainer.classList.contains('container-content')) {
        flexContainer.setAttribute('data-hidden-for-drag', 'true');
        flexContainer.style.border = 'none';
      }

      if (insertIndex >= craftChildren.length) {
        flexContainer.appendChild(placeholder);
      } else {
        flexContainer.insertBefore(placeholder, craftChildren[insertIndex]);
      }

      // Hide the original dragged element when reordering a single node within the same container,
      // so the layout shows the correct number of items (placeholder replaces original).
      // WHY: Multi-select drags emit dimensions: null and render one placeholder, so hiding all
      // dragged siblings would collapse the source container. Only hide for single-node reorders.
      if (draggedNodeDoms.length === 1) {
        const [draggedDom] = draggedNodeDoms;
        if (draggedDom && draggedDom.id !== 'drop-preview-placeholder') {
          draggedDom.setAttribute('data-hidden-for-drag', 'true');
          draggedDom.setAttribute('data-original-display', draggedDom.style.display);
          draggedDom.setAttribute('data-original-visibility', draggedDom.style.visibility);
          draggedDom.setAttribute('data-original-border', draggedDom.style.border);
          draggedDom.style.display = 'none';
        }
      }
    },
    [
      indicator,
      parentFlexDirection,
      parentChildCount,
      isPageParent,
      cleanup,
      draggedElementDisplayName,
      draggedElementDimensions,
      draggedNodeDoms,
    ],
  );

  useEffect(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (!indicator || !enabled) {
      cleanup();
      lastStateRef.current = '';
      pendingStateRef.current = '';
      lastParentIdRef.current = null;
      return;
    }

    const { placement, error } = indicator;
    const isBlankPage = isPageParent && parentChildCount === 0;
    const isPopupDrag = draggedElementDisplayName === POPUP_NODE_NAME;

    if (!placement || !placement.parent?.dom) {
      cleanup();
      lastStateRef.current = '';
      lastParentIdRef.current = null;
      return;
    }

    // Block error drops except for blank page and popup
    if (error && !isBlankPage && !isPopupDrag) {
      cleanup();
      lastStateRef.current = '';
      lastParentIdRef.current = null;
      return;
    }

    const stateKey = `${parentId}-${placement.where}-${placement.index}-${parentChildCount}-${isPageParent}-${draggedElementDisplayName || 'none'}`;

    if (stateKey === lastStateRef.current) {
      return;
    }

    // Check for existing placeholder OR highlighted container
    const existingPlaceholder = document.getElementById('drop-preview-placeholder');
    const existingHighlight = document.querySelector('[data-drop-target]');
    const hasExistingIndicator = existingPlaceholder || existingHighlight;

    // First time showing placeholder - use initial delay
    if (!hasExistingIndicator) {
      pendingStateRef.current = stateKey;
      debounceTimerRef.current = setTimeout(() => {
        if (pendingStateRef.current === stateKey) {
          lastParentIdRef.current = parentId;
          rafRef.current = requestAnimationFrame(() => {
            updatePlaceholder(stateKey);
          });
        }
      }, INITIAL_SHOW_DELAY_MS);
      return;
    }

    const isParentChange = lastParentIdRef.current !== null && lastParentIdRef.current !== parentId;
    const debounceTime = isParentChange ? PARENT_CHANGE_DEBOUNCE_MS : POSITION_CHANGE_DEBOUNCE_MS;

    pendingStateRef.current = stateKey;

    debounceTimerRef.current = setTimeout(() => {
      if (pendingStateRef.current === stateKey) {
        lastParentIdRef.current = parentId;
        rafRef.current = requestAnimationFrame(() => {
          updatePlaceholder(stateKey);
        });
      }
    }, debounceTime);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [
    indicator,
    enabled,
    parentFlexDirection,
    parentChildCount,
    parentId,
    isPageParent,
    cleanup,
    updatePlaceholder,
    draggedElementDisplayName,
  ]);

  useEffect(() => {
    return () => {
      cleanup();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [cleanup]);

  return null;
};

export default DropTargetIndicator;
